// ============================================================================
// Annual Increases (inflationary kickers) — finance queue.
//
// Ben 2026-07-02: the annual kicker is DISTINCT from the monthly management
// fee. Jose reviews upcoming increase dates in 30/60/90 buckets and either
// VALIDATES or DEFERS to a new date (Bryan's courtesy extensions — dates are
// never locked to the anniversary).
//
// 2026-10-08, item #706 (Ben: Decisions 1A, 2A, 3A): VALIDATE SETS THE RATE.
// It opens the three-step window in ./annual-increase-window.tsx — confirm the
// date by Bryan's rule, set each service line's new rate, review — and saves
// through validate_kicker(facility, date, lines, note, kv_id), which writes each
// line through record_rate_change (the one door) linked to the annual increase.
// It used to record a percentage and move the date while the rate stayed put.
//
// The second tab lists increases validated BEFORE that change with no rate
// change linked to them, and opens the same window in "finish" mode. It is a
// read of two tables with one predicate; the five that already had their change
// on record were linked by migration on 2026-10-08, so they are not here.
//
// 2026-10-20, item #710 (TCSD-0378 option B, TCSD-0438 A, Ben 14 Oct: "Day one
// of a contract start is the date of the anniversary. Hospital HOURLY rates
// increase annually, so hospitals still have an annual kicker. KC office an
// MAVs have no increase."): the third tab, "Needs a first increase date"
// (./first-increase-tab.tsx), lists every Active building with a yearly
// increase and no date, pre-filled by Bryan's rule from the next anniversary,
// and saves through defer_kicker. Upcoming never showed them: its view drops a
// building with no date.
//
// Data: v_upcoming_kickers (security_invoker) + kicker_validations +
// rate_change_proposals; validate_kicker/defer_kicker RPCs
// (finance/executive/superuser gate lives in the DB).
// ============================================================================

import { canWorkAnnualIncreases } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Loader2 } from 'lucide-react';
import {
  PageHeader,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  TabBar,
  useToast,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { overlayClickClose } from '../../shared/overlay-close';
import { AnnualIncreaseWindow, type WindowMode } from './annual-increase-window';
import { FirstIncreaseList, loadFirstDateRows, type FirstDateRow } from './first-increase-tab';
import {
  fmtLongDate,
  fmtMoney2,
  fmtShortDate,
  scheduledIncreaseFrom,
  type ScheduledIncrease,
} from './lib/annual-increase';


interface KickerRow {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  account_name: string | null;
  monthly_rate: number | null;
  management_fee_pct: number | null;
  annual_kicker_pct: number | null;
  contract_start: string | null;
  last_kicker_date: string | null;
  next_kicker_date: string;
  kicker_seed_pending: boolean;
  days_until: number;
  bucket: 'Overdue' | '30' | '60' | '90' | 'Later';
}

interface NotSetRow {
  kv_id: string;
  facility_id: string;
  kicker_date: string;
  acted_at: string;
}

interface BookedRow {
  facility_id: string;
  status: string;
  kv_id: string | null;
  field_name: string;
  effective_date: string | null;
  current_value: number | null;
  proposed_value: number;
}

const BUCKET_LABELS: Record<string, string> = {
  Overdue: 'Overdue',
  '30': 'Next 30 days',
  '60': '31–60 days',
  '90': '61–90 days',
  Later: 'Later',
};

function fmtDate(s: string | null): string {
  if (!s) return '—';
  try {
    return new Date(`${s}T00:00:00`).toLocaleDateString();
  } catch {
    return s;
  }
}
function fmtMoney(n: number | null): string {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}
// Fraction (0.0300) → percent label ("3%").
function fmtPct(n: number | null): string {
  if (n == null) return '—';
  const p = Math.round(n * 10000) / 100;
  return `${p}%`;
}

type Tab = 'upcoming' | 'not-set' | 'first-date';

export default function KickersPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const role = profile?.role ?? null;
  const allowed = canWorkAnnualIncreases(role);

  const [rows, setRows] = useState<KickerRow[]>([]);
  const [notSet, setNotSet] = useState<NotSetRow[]>([]);
  const [booked, setBooked] = useState<BookedRow[]>([]);
  const [firstDate, setFirstDate] = useState<FirstDateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('upcoming');
  const [deferRow, setDeferRow] = useState<KickerRow | null>(null);
  const [windowFor, setWindowFor] = useState<{ row: KickerRow; mode: WindowMode } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    // ⛔ A failed first-date read leaves no form that can save (item #710): it
    // fails the whole load, like any other read here.
    const firstDateLoad = loadFirstDateRows().then(
      (r) => ({ rows: r, error: null as string | null }),
      (e: unknown) => ({ rows: [] as FirstDateRow[], error: e instanceof Error ? e.message : String(e) }),
    );
    const [q, kv, links, fd] = await Promise.all([
      supabase.from('v_upcoming_kickers').select('*').order('next_kicker_date', { ascending: true }),
      supabase
        .from('kicker_validations')
        .select('kv_id, facility_id, kicker_date, acted_at')
        .eq('action', 'Validated')
        .order('kicker_date', { ascending: true }),
      supabase
        .from('rate_change_proposals')
        .select('facility_id, status, kv_id, field_name, effective_date, current_value, proposed_value')
        .not('kv_id', 'is', null),
      firstDateLoad,
    ]);
    const e = q.error?.message ?? kv.error?.message ?? links.error?.message ?? fd.error;
    if (e) {
      // ⛔ No half-loaded queue: a missing link list would show every validated
      // increase as "rate not set".
      setError(e);
      setRows([]);
      setNotSet([]);
      setBooked([]);
      setFirstDate([]);
    } else {
      setFirstDate(fd.rows);
      const linked = new Set(((links.data ?? []) as BookedRow[]).map((r) => r.kv_id));
      setRows((q.data ?? []) as KickerRow[]);
      // Validated, rate not set: a Validated annual increase with no rate change
      // linked to it.
      setNotSet(((kv.data ?? []) as NotSetRow[]).filter((r) => !linked.has(r.kv_id)));
      setBooked((links.data ?? []) as BookedRow[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  const byFacility = useMemo(() => new Map(rows.map((r) => [r.facility_id, r])), [rows]);
  const scheduledFor = useCallback(
    (r: KickerRow): ScheduledIncrease | null =>
      scheduledIncreaseFrom(booked.filter((b) => b.facility_id === r.facility_id), r.monthly_rate),
    [booked],
  );
  const seeded = useMemo(() => rows.filter((r) => r.kicker_seed_pending), [rows]);
  const confirmed = useMemo(() => rows.filter((r) => !r.kicker_seed_pending), [rows]);
  const buckets = useMemo(() => {
    const order: KickerRow['bucket'][] = ['Overdue', '30', '60', '90', 'Later'];
    return order
      .map((b) => ({ bucket: b, items: confirmed.filter((r) => r.bucket === b) }))
      .filter((g) => g.items.length > 0);
  }, [confirmed]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <EmptyState
          title="Finance access required"
          body="The annual-increase queue is visible to finance, executive, and superuser roles."
        />
      </div>
    );
  }

  const openValidate = (r: KickerRow) => setWindowFor({ row: r, mode: { kind: 'validate' } });
  const openFinish = (n: NotSetRow) => {
    const r = byFacility.get(n.facility_id);
    if (!r) return;
    setWindowFor({
      row: r,
      mode: { kind: 'finish', kv_id: n.kv_id, kicker_date: n.kicker_date, acted_on: n.acted_at.slice(0, 10) },
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title="Annual Increases"
        meta={`${rows.length} upcoming · ${seeded.length} seeded awaiting review`}
      />
      <p className="text-xs text-tcs-text-muted">
        Validating an increase sets the new rate on each service line, starting on the 1st of the month the rule
        gives. The account card and rate history update with it. Defer moves the date — courtesy extensions are fine.
      </p>

      {!loading && !error && notSet.length > 0 && tab === 'upcoming' && (
        <div className="flex flex-col gap-2 rounded-md border border-st-warn-border-strong bg-st-warn-bg px-3 py-2 sm:flex-row sm:items-center">
          <div className="text-sm">
            <p className="font-semibold">
              {notSet.length} increase{notSet.length === 1 ? ' was' : 's were'} validated but no rate change is on
              record
            </p>
            <p className="text-xs text-tcs-text-secondary">
              Validated before Validate could set a rate. Set the new rate, or record that it already happened.
            </p>
          </div>
          <Button variant="ghost" size="sm" className="sm:ml-auto" onClick={() => setTab('not-set')}>
            Review {notSet.length === 1 ? 'it' : `these ${notSet.length}`}
          </Button>
        </div>
      )}

      <TabBar<Tab>
        tabs={[
          { id: 'upcoming', label: 'Upcoming' },
          { id: 'not-set', label: 'Validated, rate not set', count: notSet.length },
          { id: 'first-date', label: 'Needs a first increase date', count: firstDate.length },
        ]}
        activeId={tab}
        onChange={setTab}
      />

      {error && <ErrorState title="Couldn't load the queue" body={error} onRetry={load} />}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14" block />
          ))}
        </div>
      ) : error ? null : tab === 'first-date' ? (
        <FirstIncreaseList
          rows={firstDate}
          onSaved={(r, date) => {
            toast.success(
              `${r.facility_name ?? r.facility_id}: first increase set for ${fmtLongDate(date)}. It is now on the Upcoming tab.`,
            );
            void load();
          }}
        />
      ) : tab === 'not-set' ? (
        notSet.length === 0 ? (
          <EmptyState title="Nothing left here" body="Every validated increase has its rate change on record." />
        ) : (
          <ul className="space-y-2">
            {notSet.map((n) => {
              const r = byFacility.get(n.facility_id);
              return (
                <Card key={n.kv_id} className="p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-tcs-text">
                        {r?.facility_name ?? n.facility_id}
                      </div>
                      <div className="truncate text-xs text-tcs-text-secondary">
                        {r?.account_name ?? '—'} · rate {fmtMoney(r?.monthly_rate ?? null)}/mo · anniversary{' '}
                        {fmtDate(n.kicker_date)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded bg-st-warn-bg-strong px-1.5 py-0.5 text-xs font-medium text-st-warn-fg-strong">
                        Validated {fmtShortDate(n.acted_at.slice(0, 10))}, rate not set
                      </span>
                      <button
                        type="button"
                        onClick={() => openFinish(n)}
                        disabled={!r}
                        title={r ? undefined : 'This building is no longer on the upcoming queue.'}
                        className="rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium hover:bg-tcs-bg-muted disabled:opacity-50"
                      >
                        Set rate
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </ul>
        )
      ) : rows.length === 0 ? (
        <EmptyState title="No upcoming increases" body="Facilities appear here once they have a next increase date." />
      ) : (
        <>
          {seeded.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-semibold text-st-warn-fg-strong">
                Seeded from contract anniversaries — needs your review ({seeded.length})
              </h2>
              <p className="mb-2 text-xs text-tcs-text-muted">
                These dates were estimated from the contract start on 2026-07-02. Validate sets the new rate on the
                date the rule gives; defer if the date is wrong.
              </p>
              <ul className="space-y-2">
                {seeded.map((r) => (
                  <KickerCard
                    key={r.facility_id}
                    row={r}
                    scheduled={scheduledFor(r)}
                    onValidate={() => openValidate(r)}
                    onDefer={() => setDeferRow(r)}
                    seeded
                  />
                ))}
              </ul>
            </section>
          )}

          {buckets.map((g) => (
            <section key={g.bucket}>
              <h2 className="mb-2 text-sm font-semibold text-tcs-text">
                {BUCKET_LABELS[g.bucket]} ({g.items.length})
              </h2>
              <ul className="space-y-2">
                {g.items.map((r) => (
                  <KickerCard
                    key={r.facility_id}
                    row={r}
                    scheduled={scheduledFor(r)}
                    onValidate={() => openValidate(r)}
                    onDefer={() => setDeferRow(r)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {deferRow && (
        <DeferModal
          row={deferRow}
          onClose={() => setDeferRow(null)}
          onDone={(msg) => {
            setDeferRow(null);
            toast.success(msg);
            void load();
          }}
        />
      )}

      {windowFor && (
        <AnnualIncreaseWindow
          building={windowFor.row}
          mode={windowFor.mode}
          onClose={() => setWindowFor(null)}
          onDone={() => {
            setWindowFor(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function KickerCard({
  row,
  seeded = false,
  scheduled,
  onValidate,
  onDefer,
}: {
  row: KickerRow;
  seeded?: boolean;
  scheduled: ScheduledIncrease | null;
  onValidate: () => void;
  onDefer: () => void;
}) {
  const overdue = row.bucket === 'Overdue';
  return (
    <Card className={`p-3 ${seeded ? 'border-st-warn-border-strong bg-st-warn-bg/50' : ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-tcs-text">
            {row.facility_name ?? row.facility_id}
          </div>
          <div className="truncate text-xs text-tcs-text-secondary">
            {row.account_name ?? row.account_id ?? '—'} · rate {fmtMoney(row.monthly_rate)}/mo
            {` · ${fmtPct(row.annual_kicker_pct)} increase`}
            {row.contract_start ? ` · started ${fmtDate(row.contract_start)}` : ''}
          </div>
          {scheduled && (
            <span className="mt-1 inline-block rounded-full bg-st-info-bg px-2 py-0.5 text-xs text-st-info-fg-strong">
              Increase scheduled {fmtShortDate(scheduled.on)}: {fmtMoney2(scheduled.newTotal)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium ${
              overdue
                ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
                : row.bucket === '30'
                  ? 'bg-st-warn-bg-strong text-st-warn-fg-strong'
                  : 'bg-tcs-surface text-tcs-text'
            }`}
          >
            <CalendarClock size={12} aria-hidden />
            {fmtDate(row.next_kicker_date)}
            {overdue ? ` · ${Math.abs(row.days_until)}d overdue` : ` · in ${row.days_until}d`}
          </span>
          <button
            type="button"
            onClick={onValidate}
            className="rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs font-medium hover:bg-tcs-bg-muted"
          >
            Validate
          </button>
          <button
            type="button"
            onClick={onDefer}
            className="rounded-md border border-tcs-border bg-tcs-raised px-2 py-1 text-xs hover:bg-tcs-bg-muted"
          >
            Defer
          </button>
        </div>
      </div>
    </Card>
  );
}

// Defer is unchanged by item #706.
function DeferModal({
  row,
  onClose,
  onDone,
}: {
  row: KickerRow;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [newDate, setNewDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    const { error: e } = await supabase.rpc('defer_kicker', {
      p_facility_id: row.facility_id,
      p_new_date: newDate,
      p_notes: notes.trim() || undefined,
    });
    if (e) {
      setError(rpcErrorMessage(e, 'Could not save. Try again, and if it persists tell the COO.'));
      setBusy(false);
      return;
    }
    onDone(`Deferred to ${fmtDate(newDate)}`);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:w-[480px] sm:rounded-xl"
        role="dialog"
        aria-modal="true"
      >
        <h3 className="text-sm font-semibold text-tcs-text">Defer annual increase</h3>
        <p className="mt-1 text-xs text-tcs-text-secondary">
          {row.facility_name ?? row.facility_id} · due {fmtDate(row.next_kicker_date)}
        </p>
        <label className="mt-4 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            New increase date
          </span>
          <input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </label>
        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Notes (optional)
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="e.g. courtesy extension per the COO"
            className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
          />
        </label>
        {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={submit} disabled={busy || !newDate}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Defer
          </Button>
        </div>
      </div>
    </div>
  );
}
